import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { RequestConfig, FileUploadConfig, RetryConfig } from '../types/api';
import { ApiError, ApiResponse } from '../types/common';
import { record403, recordAuthSuccess } from './authCircuitBreaker';

// Endpoints called by background timers / interval polls. A 403 from any of
// these is non-actionable for the user and shouldn't push the auth circuit
// breaker toward a forced logout. Match by substring; keep narrow.
const BACKGROUND_POLL_PATHS = [
  '/student/activity/log',
  '/notifications/count',
];

// ===== Token Refresh Queue System =====
// Prevents multiple simultaneous token refresh calls
// All failed requests are queued and retried after refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (error?: any) => void;
}> = [];

const processQueue = (error: any = null) => {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve();
    }
  });
  failedQueue = [];
};

/**
 * Handle 401 Unauthorized errors with token refresh
 * Uses request queueing to prevent multiple refresh calls
 * Retries original request after successful refresh
 */
const handle401Error = (error: AxiosError, config: AxiosRequestConfig | undefined): Promise<any> => {
  const originalRequest = config as any;

  // Prevent infinite loops - if refresh already attempted, logout
  if (originalRequest._retry) {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('tokenExpiry');

    // Dispatch logout event for navigation
    window.dispatchEvent(new CustomEvent('auth:logout'));

    return Promise.reject({
      type: 'http',
      status: 401,
      message: 'Session expired. Please login again.'
    } as ApiError);
  }

  originalRequest._retry = true;

  // If already refreshing, queue this request
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then(() => httpClient(originalRequest))
      .catch(err => Promise.reject(err));
  }

  // Start refresh
  isRefreshing = true;

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    // No refresh token - logout
    window.dispatchEvent(new CustomEvent('auth:logout'));
    return Promise.reject({
      type: 'http',
      status: 401,
      message: 'No refresh token available'
    } as ApiError);
  }

  // Attempt to refresh token
  return new Promise((resolve, reject) => {
    // Dynamic import to avoid circular dependency
    import('./authService').then(({ authService }) => {
      authService.refreshToken(refreshToken)
        .then(response => {
          // Update access token in localStorage
          localStorage.setItem('authToken', response.accessToken);

          // Update request header with new token
          originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;

          // Process queued requests
          processQueue(null);

          // Retry original request with new token
          resolve(httpClient(originalRequest));
        })
        .catch(refreshError => {
          // Refresh failed - logout user
          processQueue(refreshError);
          window.dispatchEvent(new CustomEvent('auth:logout'));
          reject(refreshError);
        })
        .finally(() => {
          isRefreshing = false;
        });
    })
    .catch(importError => {
      // Failed to import authService
      isRefreshing = false;
      window.dispatchEvent(new CustomEvent('auth:logout'));
      reject(importError);
    });
  });
};

// Default timeout for regular requests; uploads get a longer window (see interceptor).
const DEFAULT_TIMEOUT = 10000; // 10 seconds
const UPLOAD_TIMEOUT = 300000; // 5 minutes

// Create axios instance with base configuration
const httpClient: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api',
  timeout: DEFAULT_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for authentication
httpClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    // Get auth token from localStorage or auth store
    // Skip auth for public endpoints to prevent token refresh issues
    const token = localStorage.getItem('authToken');
    const isPublicEndpoint = config.url?.startsWith('/public/');
    if (token && !isPublicEndpoint) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // File uploads (FormData bodies) go through the Firebase proxy in prod
    // (cold start + extra hop) and can easily exceed the 10s default timeout.
    // Bump uploads to a longer window — but only when the caller hasn't set
    // an explicit timeout of their own, so per-request overrides still win.
    const isUpload =
      typeof FormData !== 'undefined' && config.data instanceof FormData;
    if (isUpload && (config.timeout === undefined || config.timeout === DEFAULT_TIMEOUT)) {
      config.timeout = UPLOAD_TIMEOUT;
    }

    // Add request timestamp for debugging
    if (config.metadata === undefined) {
      config.metadata = {};
    }
    config.metadata.startTime = new Date();

    return config;
  },
  (error: AxiosError): Promise<AxiosError> => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
httpClient.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => {
    // Calculate request duration for performance monitoring
    const endTime = new Date();
    const duration = response.config.metadata?.startTime
      ? endTime.getTime() - response.config.metadata.startTime.getTime()
      : 0;

    // Log successful requests in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url} - ${duration}ms`);
    }

    // Reset the 403 circuit breaker on any successful response
    recordAuthSuccess();

    return response;
  },
  (error: AxiosError): Promise<ApiError> => {
    const { response, request, config } = error;

    // Calculate request duration even for errors
    const endTime = new Date();
    const duration = config?.metadata?.startTime
      ? endTime.getTime() - config.metadata.startTime.getTime()
      : 0;

    // Log errors in development
    if (process.env.NODE_ENV === 'development') {
      console.error(`❌ ${config?.method?.toUpperCase()} ${config?.url} - ${duration}ms`, error);
    }

    // Handle different error types
    if (response) {
      // Server responded with error status
      const { status, data } = response;
      // Cast to any to handle flexible error response structure
      const errorData = data as any;
      // Skip auth handling for public endpoints to prevent dashboard redirects
      const isPublicRequest = config?.url?.startsWith('/public/');

      switch (status) {
        case 401:
          // Unauthorized - attempt token refresh (skip for public endpoints).
          // Special case: if the failing call IS the logout endpoint, skip
          // the refresh-and-retry dance entirely. The user is logging out;
          // refreshing then re-calling /auth/logout creates a recursive
          // loop because the App-level auth:logout handler also calls
          // logout(). Just clear tokens and dispatch the event once.
          if (!isPublicRequest) {
            const isLogoutCall = (config?.url || '').includes('/auth/logout');
            if (isLogoutCall) {
              localStorage.removeItem('authToken');
              localStorage.removeItem('refreshToken');
              localStorage.removeItem('tokenExpiry');
              window.dispatchEvent(new CustomEvent('auth:logout'));
              return Promise.reject({
                type: 'http',
                status: 401,
                message: 'Session already expired',
              } as ApiError);
            }
            return handle401Error(error, config);
          }
          break;

        case 403:
          // Forbidden - dispatch event for role-based redirect (skip for public endpoints)
          if (!isPublicRequest) {
            // Background polling endpoints (heartbeats, unread counts, etc.)
            // shouldn't drive the auth circuit breaker — a permission gap
            // there is benign and surfacing it as a logout punishes users
            // for code that polls universally but is gated server-side.
            // Foreground 403s still count.
            const requestUrl = (config?.url || '').toString();
            const isBackgroundPoll = BACKGROUND_POLL_PATHS.some((p) =>
              requestUrl.includes(p)
            );

            if (isBackgroundPoll) {
              // Silently drop — no toast, no breaker increment.
              break;
            }

            const { tripped, count } = record403({
              method: config?.method,
              url: config?.url,
              status,
              serverMessage: errorData?.message,
            });

            if (tripped) {
              // Circuit breaker already cleared tokens and fired auth:logout.
              // Fall through to the generic reject so the pending call resolves.
              showErrorNotification(
                'Signed out after repeated permission errors. Check the console for the diagnostic log (window.__getAuthErrorLog()).'
              );
            } else {
              window.dispatchEvent(new CustomEvent('auth:forbidden'));
              showErrorNotification(
                `You do not have permission to access this resource. (${count}/3)`
              );
            }
          }
          break;
        case 404:
          // Not found
          showErrorNotification('The requested resource was not found.');
          break;
        case 422:
          // Validation error - return validation messages
          const validationErrors = errorData?.errors || {};
          return Promise.reject({
            type: 'validation',
            errors: validationErrors,
            message: errorData?.message || 'Validation failed',
            status,
            data,
          } as ApiError);
        case 429:
          // Rate limiting
          showErrorNotification('Too many requests. Please try again later.');
          break;
        case 500:
          // Server error
          showErrorNotification('Internal server error. Please try again later.');
          break;
        default:
          // Generic error
          showErrorNotification(errorData?.message || 'An unexpected error occurred.');
      }

      return Promise.reject({
        type: 'http',
        status,
        // Backend errors come back as either { message } or { error: { message } }.
        // Read both so callers see the real reason instead of a generic "HTTP Error".
        message: errorData?.message || errorData?.error?.message || 'HTTP Error',
        data,
      } as ApiError);
    } else if (request) {
      // Request was made but no response received (network error)
      showErrorNotification('Network error. Please check your connection and try again.');
      return Promise.reject({
        type: 'network',
        message: 'Network error',
      } as ApiError);
    } else {
      // Something else happened
      showErrorNotification('An unexpected error occurred.');
      return Promise.reject({
        type: 'unknown',
        message: error.message || 'Unknown error',
      } as ApiError);
    }
  }
);

// Utility function to show error notifications
const showErrorNotification = (message: string): void => {
  // This will be integrated with your notification system
  // For now, just console.error in development
  if (process.env.NODE_ENV === 'development') {
    console.error('HTTP Client Error:', message);
  }

  // You can dispatch to a notification store or trigger a toast here
  // Example: useNotificationStore.getState().showError(message);
};

// Retry mechanism for failed requests
const retryRequest = async <T>(
  originalRequest: AxiosRequestConfig,
  retries: number = 3,
  delay: number = 1000
): Promise<AxiosResponse<T>> => {
  for (let i = 0; i < retries; i++) {
    try {
      const response = await httpClient(originalRequest);
      return response;
    } catch (error) {
      const apiError = error as ApiError;
      if (i === retries - 1 || (apiError.status && apiError.status < 500)) {
        throw error;
      }
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i)));
    }
  }
  throw new Error('Max retries exceeded');
};

// Enhanced HTTP methods with automatic retry for network errors
const httpMethods = {
  get: async <T = any>(url: string, config: RequestConfig = {}): Promise<T> => {
    try {
      const response = await httpClient.get<T>(url, config);
      return response.data;
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.type === 'network') {
        const response = await retryRequest<T>({ method: 'get', url, ...config });
        return response.data;
      }
      throw error;
    }
  },

  post: async <T = any>(url: string, data?: any, config: RequestConfig = {}): Promise<T> => {
    try {
      const response = await httpClient.post<T>(url, data, config);
      return response.data;
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.type === 'network') {
        const response = await retryRequest<T>({ method: 'post', url, data, ...config });
        return response.data;
      }
      throw error;
    }
  },

  put: async <T = any>(url: string, data?: any, config: RequestConfig = {}): Promise<T> => {
    try {
      const response = await httpClient.put<T>(url, data, config);
      return response.data;
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.type === 'network') {
        const response = await retryRequest<T>({ method: 'put', url, data, ...config });
        return response.data;
      }
      throw error;
    }
  },

  patch: async <T = any>(url: string, data?: any, config: RequestConfig = {}): Promise<T> => {
    try {
      const response = await httpClient.patch<T>(url, data, config);
      return response.data;
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.type === 'network') {
        const response = await retryRequest<T>({ method: 'patch', url, data, ...config });
        return response.data;
      }
      throw error;
    }
  },

  delete: async <T = any>(url: string, config: RequestConfig = {}): Promise<T> => {
    try {
      const response = await httpClient.delete<T>(url, config);
      return response.data;
    } catch (error) {
      const apiError = error as ApiError;
      if (apiError.type === 'network') {
        const response = await retryRequest<T>({ method: 'delete', url, ...config });
        return response.data;
      }
      throw error;
    }
  },
};

// File upload helper
export const uploadFile = async <T = any>(
  url: string,
  file: File,
  config: FileUploadConfig = {}
): Promise<T> => {
  const formData = new FormData();
  formData.append('file', file);

  const uploadConfig: AxiosRequestConfig = {
    ...config,
    headers: {
      'Content-Type': 'multipart/form-data',
      ...config.headers,
    },
  };

  if (config.onProgress) {
    uploadConfig.onUploadProgress = (progressEvent) => {
      if (progressEvent.total) {
        const progress = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        config.onProgress!(progress);
      }
    };
  }

  const response = await httpClient.post<T>(url, formData, uploadConfig);
  return response.data;
};

// Request cancellation helper
export const createCancelToken = () => {
  return axios.CancelToken.source();
};

// Check if error is a cancellation
export const isCancel = (error: any): boolean => {
  return axios.isCancel(error);
};

// Type declaration for axios config metadata and retry flag
declare module 'axios' {
  interface AxiosRequestConfig {
    metadata?: {
      startTime?: Date;
    };
    _retry?: boolean;
  }
  interface InternalAxiosRequestConfig {
    metadata?: {
      startTime?: Date;
    };
    _retry?: boolean;
  }
}

export default httpMethods;